import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  Box,
  Alert,
  IconButton,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Avatar,
  Slide,
  Link,
  Grid
} from '@mui/material';
import {
  Google as GoogleIcon,
  Facebook as FacebookIcon,
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

export default function Auth() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const { loginWithEmail, loginWithGoogle, loginWithFacebook, signup, resetPassword } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const validate = (values) => {
    const errors = {};

    // Email validation
    if (!values.email) {
      errors.email = t('auth.validation.email.required');
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = t('auth.validation.email.invalid');
    }

    // Password validation
    if (!values.password) {
      errors.password = t('auth.validation.password.required');
    } else if (values.password.length < 6) {
      errors.password = t('auth.validation.password.minLength');
    }

    // Name validation (for signup)
    if (!isLogin && !values.name) {
      errors.name = t('auth.validation.name.required');
    }

    // Phone validation (for signup)
    if (!isLogin && !values.phone) {
      errors.phone = t('auth.validation.phone.required');
    } else if (!isLogin && !/^[0-9]{10}$/.test(values.phone)) {
      errors.phone = t('auth.validation.phone.invalid');
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      name: '',
      phone: '',
    },
    validate,
    onSubmit: async (values) => {
      try {
        if (isLogin) {
          await loginWithEmail(values.email, values.password);
        } else {
          await signup(values.email, values.password, {
            name: values.name,
            phone: values.phone,
          });
        }
        navigate('/');
      } catch (err) {
        setError(err.message);
      }
    },
  });

  const handleSocialLogin = async (provider) => {
    try {
      if (provider === 'google') {
        await loginWithGoogle();
      } else if (provider === 'facebook') {
        await loginWithFacebook();
      }
      navigate('/');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await resetPassword(resetEmail);
      enqueueSnackbar(t('auth.resetEmailSent'), { variant: 'success' });
      setForgotMode(false);
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  return (
    <Container maxWidth="sm" sx={{ 
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      py: 4
    }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4,
          width: '100%',
          borderRadius: 2,
          background: theme.palette.background.paper
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Avatar 
            sx={{ 
              width: 60, 
              height: 60, 
              margin: '0 auto',
              bgcolor: theme.palette.primary.main
            }}
          >
            {isLogin ? <LockIcon /> : <PersonIcon />}
          </Avatar>
          <Typography variant="h4" sx={{ mt: 2, fontWeight: 'bold' }}>
            {isLogin ? t('common.welcome') : t('common.createAccount')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {isLogin 
              ? t('common.signInPrompt')
              : t('common.signUpPrompt')
            }
          </Typography>
        </Box>

        {error && (
          <Slide direction="down" in={!!error}>
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          </Slide>
        )}

        {forgotMode ? (
          <>
            <Typography variant="h5" gutterBottom>
              {t('auth.resetPassword')}
            </Typography>
            <Box component="form" onSubmit={handleResetPassword} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="resetEmail"
                label={t('auth.email')}
                name="email"
                autoComplete="email"
                autoFocus
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                {t('auth.sendResetLink')}
              </Button>
              <Button
                fullWidth
                onClick={() => setForgotMode(false)}
              >
                {t('common.back')}
              </Button>
            </Box>
          </>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            {!isLogin && (
              <TextField
                fullWidth
                name="name"
                label={t('auth.fullName')}
                margin="normal"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            <TextField
              fullWidth
              name="email"
              label={t('auth.email')}
              margin="normal"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              name="password"
              label={t('auth.password')}
              type={showPassword ? 'text' : 'password'}
              margin="normal"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {!isLogin && (
              <TextField
                fullWidth
                name="phone"
                label={t('auth.phoneNumber')}
                margin="normal"
                value={formik.values.phone}
                onChange={formik.handleChange}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            <Grid container>
              <Grid item xs>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setForgotMode(true)}
                >
                  {t('auth.forgotPassword')}
                </Link>
              </Grid>
              <Grid item>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    formik.resetForm();
                    setError('');
                  }}
                  sx={{ fontWeight: 'bold' }}
                >
                  {isLogin ? t('auth.signUp') : t('auth.signIn')}
                </Link>
              </Grid>
            </Grid>

            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              size="large"
              sx={{ mt: 3, mb: 2, py: 1.2 }}
            >
              {isLogin ? t('auth.signIn') : t('auth.signUp')}
            </Button>
          </form>
        )}

        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t('common.or')}
          </Typography>
        </Divider>

        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 2, 
          mb: 3 
        }}>
          <Button
            type="button"
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={() => handleSocialLogin('google')}
            sx={{ 
              fontSize: isMobile ? '0.7rem' : '0.75rem',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: 1,
              height: 40,
              px: isMobile ? 1 : 1.5,
              '& .MuiButton-startIcon': {
                marginRight: isMobile ? 0.5 : 1
              }
            }}
          >
            {isLogin ? t('auth.signInWith.google') : t('auth.signUpWith.google')}
          </Button>
          <Button
            type="button"
            fullWidth
            variant="outlined"
            startIcon={<FacebookIcon />}
            onClick={() => handleSocialLogin('facebook')}
            sx={{ 
              fontSize: isMobile ? '0.7rem' : '0.75rem',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: 1,
              height: 40,
              px: isMobile ? 1 : 1.5,
              '& .MuiButton-startIcon': {
                marginRight: isMobile ? 0.5 : 1
              }
            }}
          >
            {isLogin ? t('auth.signInWith.facebook') : t('auth.signUpWith.facebook')}
          </Button>
        </Box>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {isLogin ? t('auth.dontHaveAccount') : t('auth.alreadyHaveAccount')}
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                setIsLogin(!isLogin);
                formik.resetForm();
              }}
              sx={{ ml: 1, fontWeight: 'bold' }}
            >
              {isLogin ? t('auth.signUp') : t('auth.signIn')}
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
} 